import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

const FRAME_INSET_OUTER = 8;
const FRAME_INSET_INNER = 10;

/**
 * "Elegante" template — double border frame, centered logo,
 * spaced uppercase company name. Serif-ish (via bold helvetica + spacing).
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - margin * 2;
  const { primaryColor, grayLight, grayText, darkText } =
    buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // Frame drawn on every page (see loop at end)
  drawFrame(doc, primaryColor, darkText);

  let yPos = margin + 4;

  // Centered logo
  if (logoBase64) {
    try {
      const logoSize = 24;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, (pageWidth - logoSize) / 2, yPos, logoSize, logoSize);
      yPos += logoSize + 6;
    } catch {
      yPos += 6;
    }
  }

  // Company name (uppercase, spaced)
  if (data.company?.name) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...darkText);
    const spaced = data.company.name.toUpperCase().split('').join(' ');
    doc.text(spaced, pageWidth / 2, yPos, { align: 'center' });
    yPos += 5;
  }

  // Company sub-info centered
  const subLines: string[] = [];
  if (data.company?.cnpj) subLines.push(`CNPJ ${data.company.cnpj}`);
  if (data.company?.address) subLines.push(data.company.address);
  const contact: string[] = [];
  if (data.company?.phone) contact.push(data.company.phone);
  if (data.company?.email) contact.push(data.company.email);
  if (contact.length) subLines.push(contact.join('   |   '));

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  subLines.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  yPos += 4;

  // Thin decorative rule
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.4);
  doc.line(pageWidth / 2 - 30, yPos, pageWidth / 2 + 30, yPos);
  yPos += 8;

  // Document title (centered, big)
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkText);
  // Centralizado e sozinho na linha: a logo deste template fica ACIMA, tambem
  // centralizada, entao a largura util e a pagina inteira menos as margens.
  const tituloCabecalho = fitHeaderTitle(doc, resolveDocumentTitle(data), contentWidth, 18);
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, pageWidth / 2, yPos, { align: 'center' });
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: pageWidth / 2, y: yPos, maxWidth: contentWidth, color: grayText, align: 'center',
  });
  yPos += 10;

  // Client info block (two columns)
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('CLIENTE', margin, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(data.client.name || 'Não informado', margin + 20, yPos);

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('DATA', pageWidth - margin - 45, yPos);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(formatQuoteDate(data.quoteDate), pageWidth - margin - 30, yPos);
  yPos += 5;

  if (data.client.phone) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('TELEFONE', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(data.client.phone, margin + 20, yPos);
    yPos += 5;
  }
  if (data.client.document) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ' : 'CPF', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(formatDocument(data.client.document), margin + 20, yPos);
    yPos += 5;
  }
  if (data.client.address) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('ENDEREÇO', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const line = doc.splitTextToSize(data.client.address, contentWidth - 25)[0];
    doc.text(line, margin + 20, yPos);
    yPos += 5;
  }
  if (data.validityDays && data.quoteDate) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('VALIDADE', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(`até ${calcValidityDate(data.quoteDate, data.validityDays)}`, margin + 20, yPos);
    yPos += 5;
  }
  if (data.reference) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text('REFERÊNCIA', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    doc.text(doc.splitTextToSize(data.reference, contentWidth - 25)[0], margin + 20, yPos);
    yPos += 5;
  }
  yPos += 4;

  // === ITEMS TABLE (thin lines, no zebra) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 14, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 26, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 28, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 16, halign: 'center' as const },
        2: { cellWidth: 12, halign: 'center' as const },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: darkText,
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
      halign: 'center',
      lineColor: darkText,
      lineWidth: 0.2,
    },
    bodyStyles: {
      fontSize: 8,
      cellPadding: 3,
      lineColor: grayLight,
      lineWidth: 0.15,
    },
    columnStyles,
    margin: { left: margin, right: margin },
    didDrawPage: () => drawFrame(doc, primaryColor, darkText),
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 6;

  // === TOTAL ===
  const totals = pdfQuoteTotals(data);
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: 70,
  });

  doc.setDrawColor(...darkText);
  doc.setLineWidth(0.3);
  const totalX = pageWidth - margin - 70;
  doc.line(totalX, yPos, pageWidth - margin, yPos);
  yPos += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  doc.text('TOTAL A PAGAR', totalX, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...darkText);
  doc.text(formatCurrency(totals.total), pageWidth - margin, yPos, { align: 'right' });
  yPos += 3;
  doc.line(totalX, yPos, pageWidth - margin, yPos);
  yPos += 10;

  if (data.warranty || data.notes) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('OBSERVAÇÕES', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    if (data.warranty) {
      const lines = doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 2;
    }
    if (data.notes) {
      const lines = doc.splitTextToSize(data.notes, contentWidth);
      doc.text(lines, margin, yPos);
      yPos += 4 * lines.length + 4;
    }
  }

  // Formas aceitas + condições de pagamento. O retorno avança o `yPos`:
  // tudo abaixo (PIX, fotos, assinatura) é posicionado por coordenada.
  // === DIAGNÓSTICO TÉCNICO (Ordem de Serviço) ===
  // Só desenha quando a OS tem os campos preenchidos; devolve o yPos avançado
  // (jsPDF não tem layout — armadilha #13).
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  if (data.termsConditions) {
    if (yPos > pageHeight - 70) {
      doc.addPage();
      drawFrame(doc, primaryColor, darkText);
      yPos = margin + 6;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text('TERMOS E CONDIÇÕES', margin, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth);
    doc.text(lines, margin, yPos);
    yPos += 3.5 * lines.length + 4;
  }

  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    elegant: true,
    contentBottom: yPos,
  });

  // Ensure frame drawn on every page
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawFrame(doc, primaryColor, darkText);
  }

  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}

function drawFrame(
  doc: jsPDF,
  outerColor: [number, number, number],
  innerColor: [number, number, number],
): void {
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  doc.setDrawColor(...outerColor);
  doc.setLineWidth(0.6);
  doc.rect(FRAME_INSET_OUTER, FRAME_INSET_OUTER, pw - FRAME_INSET_OUTER * 2, ph - FRAME_INSET_OUTER * 2);

  doc.setDrawColor(...innerColor);
  doc.setLineWidth(0.25);
  doc.rect(FRAME_INSET_INNER, FRAME_INSET_INNER, pw - FRAME_INSET_INNER * 2, ph - FRAME_INSET_INNER * 2);
}
